import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Bell, LogOut, LayoutDashboard, Users, CalendarDays, MessageSquare, BarChart3, GraduationCap, BookMarked, User, LifeBuoy, Sparkles, UserCog, MessagesSquare, CalendarCog, Library, Camera, Menu, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/button";
import LessonTimer from "./LessonTimer";
import Logo from "./Logo";

function LogoMark() {
  return <Logo className="h-16 w-auto" testId="logo-mark" />;
}

function NavItem({ to, icon: Icon, label, testId, onNavigate }) {
  return (
    <NavLink
      to={to}
      end
      data-testid={testId}
      onClick={onNavigate}
      className={({ isActive }) =>
        `group flex items-center gap-3 px-4 py-3 border-l-2 transition-all ${
          isActive
            ? "border-[#38BDF8] bg-white/5 text-white"
            : "border-transparent text-[#A3A3A3] hover:text-white hover:bg-white/[0.03]"
        }`
      }
    >
      <Icon size={18} aria-hidden="true" />
      <span className="font-display uppercase text-sm tracking-wider font-bold">{label}</span>
    </NavLink>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [notifs, setNotifs] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);

  const loadNotifs = async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifs(data || []);
    } catch (_) {}
  };
  useEffect(() => {
    loadNotifs();
    const i = setInterval(loadNotifs, 30000);
    return () => clearInterval(i);
  }, []);

  // Close drawer on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Lock body scroll while mobile drawer is open
  useEffect(() => {
    if (mobileOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const unread = notifs.filter((n) => !n.read).length;
  const isCoach = user?.role === "coach";

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); loadNotifs(); } catch (_) {}
  };

  const onNavigate = () => setMobileOpen(false);

  const NavBody = (
    <>
      {isCoach ? (
        <>
          <NavItem to="/coach" icon={LayoutDashboard} label="Dashboard" testId="nav-coach-dashboard" onNavigate={onNavigate} />
          <NavItem to="/coach/students" icon={Users} label="Students" testId="nav-coach-students" onNavigate={onNavigate} />
          <NavItem to="/coach/calendar" icon={CalendarDays} label="Calendar" testId="nav-coach-calendar" onNavigate={onNavigate} />
          <NavItem to="/coach/availability" icon={CalendarCog} label="Availability" testId="nav-coach-availability" onNavigate={onNavigate} />
          <NavItem to="/coach/plans" icon={Sparkles} label="AI Plans" testId="nav-coach-plans" onNavigate={onNavigate} />
          <NavItem to="/coach/plans/library" icon={Library} label="Plan Library" testId="nav-coach-plans-library" onNavigate={onNavigate} />
          <NavItem to="/coach/capture" icon={Camera} label="Motion Capture" testId="nav-coach-capture" onNavigate={onNavigate} />
          <NavItem to="/coach/messages" icon={MessageSquare} label="Messages" testId="nav-coach-messages" onNavigate={onNavigate} />
          <NavItem to="/coach/analytics" icon={BarChart3} label="Analytics" testId="nav-coach-analytics" onNavigate={onNavigate} />
          <NavItem to="/coach/ask" icon={MessagesSquare} label="Ask AI" testId="nav-coach-ask" onNavigate={onNavigate} />
          <NavItem to="/coach/help" icon={LifeBuoy} label="Guide" testId="nav-coach-help" onNavigate={onNavigate} />
          <NavItem to="/coach/profile" icon={UserCog} label="Profile" testId="nav-coach-profile" onNavigate={onNavigate} />
        </>
      ) : (
        <>
          <NavItem to="/student" icon={LayoutDashboard} label="Dashboard" testId="nav-student-dashboard" onNavigate={onNavigate} />
          <NavItem to="/student/book" icon={BookMarked} label="Book Lesson" testId="nav-student-book" onNavigate={onNavigate} />
          <NavItem to="/student/lessons" icon={GraduationCap} label="My Lessons" testId="nav-student-lessons" onNavigate={onNavigate} />
          <NavItem to="/student/captures" icon={Camera} label="My Analyses" testId="nav-student-captures" onNavigate={onNavigate} />
          <NavItem to="/student/messages" icon={MessageSquare} label="Messages" testId="nav-student-messages" onNavigate={onNavigate} />
          <NavItem to="/student/profile" icon={UserCog} label="Profile" testId="nav-student-profile" onNavigate={onNavigate} />
          <NavItem to="/student/ask" icon={MessagesSquare} label="Ask AI" testId="nav-student-ask" onNavigate={onNavigate} />
          <NavItem to="/student/help" icon={LifeBuoy} label="Guide" testId="nav-student-help" onNavigate={onNavigate} />
        </>
      )}
    </>
  );

  const Footer = (
    <div className="p-4 border-t border-white/10">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 bg-white/10 grid place-items-center shrink-0">
          <User size={16} />
        </div>
        <div className="min-w-0">
          <div className="font-bold text-sm truncate" data-testid="sidebar-username">{user?.name}</div>
          <div className="text-[10px] uppercase tracking-widest text-[#38BDF8] font-bold">{user?.role}</div>
        </div>
      </div>
      <Button
        onClick={handleLogout}
        variant="outline"
        className="w-full rounded-none border-white/20 bg-transparent hover:bg-white/5 uppercase font-bold tracking-wider text-xs"
        data-testid="logout-button"
      >
        <LogOut size={14} className="mr-2" /> Log out
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] md:grid md:grid-cols-[260px_1fr]">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex border-r border-white/10 sticky top-0 h-screen flex-col" data-testid="sidebar">
        <div className="p-6 border-b border-white/10">
          <LogoMark />
        </div>
        <nav className="flex-1 py-4 flex flex-col overflow-y-auto">{NavBody}</nav>
        {Footer}
      </aside>

      {/* Mobile drawer */}
      <div
        className={`md:hidden fixed inset-0 z-50 ${mobileOpen ? "" : "pointer-events-none"}`}
        aria-hidden={!mobileOpen}
      >
        <div
          className={`absolute inset-0 bg-black/60 transition-opacity ${mobileOpen ? "opacity-100" : "opacity-0"}`}
          onClick={() => setMobileOpen(false)}
        />
        <aside
          className={`absolute left-0 top-0 bottom-0 w-[84vw] max-w-[320px] bg-[#0A0A0A] border-r border-white/10 flex flex-col transition-transform duration-200 ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}
          data-testid="mobile-sidebar"
        >
          <div className="p-4 border-b border-white/10 flex items-center justify-between">
            <LogoMark />
            <button
              onClick={() => setMobileOpen(false)}
              className="w-10 h-10 border border-white/10 grid place-items-center hover:bg-white/5"
              data-testid="mobile-sidebar-close"
              aria-label="Close menu"
            >
              <X size={18} />
            </button>
          </div>
          <nav className="flex-1 py-2 flex flex-col overflow-y-auto">{NavBody}</nav>
          {Footer}
        </aside>
      </div>

      {/* Main */}
      <main className="min-w-0">
        <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/60 border-b border-white/10 px-4 md:px-8 py-3 md:py-4 flex items-center justify-between gap-3" data-testid="topbar">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => setMobileOpen(true)}
              className="md:hidden w-10 h-10 border border-white/10 grid place-items-center hover:bg-white/5 shrink-0"
              data-testid="mobile-sidebar-open"
              aria-label="Open menu"
            >
              <Menu size={18} />
            </button>
            <div className="min-w-0">
              <div className="text-[9px] md:text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold">
                {isCoach ? "Coach Console" : "Athlete Portal"}
              </div>
              <div className="font-display text-lg md:text-2xl font-black uppercase tracking-tight truncate">
                <span className="hidden sm:inline">Welcome back, </span>{user?.name?.split(" ")[0]}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <Popover>
              <PopoverTrigger asChild>
                <button
                  className="relative w-10 h-10 border border-white/10 grid place-items-center hover:bg-white/5 transition-colors"
                  data-testid="notifications-button"
                  aria-label="Notifications"
                >
                  <Bell size={18} />
                  {unread > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#38BDF8] text-[#0A0A0A] text-[10px] font-black grid place-items-center" data-testid="notifications-unread-count">
                      {unread}
                    </span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-[92vw] max-w-sm rounded-none bg-[#141414] border-white/10 p-0" data-testid="notifications-panel">
                <div className="flex items-center justify-between p-3 border-b border-white/10">
                  <span className="font-display uppercase tracking-widest text-sm">Notifications</span>
                  <button className="text-xs text-[#38BDF8] hover:underline" onClick={markAll} data-testid="notifications-mark-all">
                    Mark all read
                  </button>
                </div>
                <div className="max-h-80 overflow-auto">
                  {notifs.length === 0 && (
                    <div className="p-6 text-center text-sm text-[#A3A3A3]">No notifications</div>
                  )}
                  {notifs.map((n) => (
                    <div
                      key={n.id}
                      className={`p-3 border-b border-white/5 text-sm ${!n.read ? "bg-white/[0.03]" : ""}`}
                      data-testid={`notification-${n.id}`}
                    >
                      <div className="text-white">{n.message}</div>
                      <div className="text-[10px] text-[#A3A3A3] mt-1">{new Date(n.created_at).toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </header>

        <div className="p-4 md:p-8">
          <Outlet />
        </div>
      </main>
      {isCoach && <LessonTimer />}
    </div>
  );
}
